import { formatDate } from '@angular/common';
import { Component, LOCALE_ID, computed, inject, input, model, output } from '@angular/core';

import { addCalendarDays, fromDayKey, mondayOfWeek, toDayKey } from '../../../helpers/calendar-day-helpers';
import { MonthCalendarDay } from '../../../models';

// One cell of the month grid.
interface MonthCalendarCell {
  key: string;
  dayNumber: number;
  inMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  day?: MonthCalendarDay;
}

@Component({
  selector: 'app-month-calendar',
  templateUrl: './month-calendar.component.html',
  styleUrl: './month-calendar.component.scss',
})
// A month at a glance, Monday first, with something to say about each day - its value, a
// caption, how full it is, a few tags - and days that can be SELECTED to act on together.
// Production plan, 2026-10-26: each loom's availability.
//
// Selection follows the file-list convention: click picks one day, Ctrl/Cmd-click adds or
// removes a day, Shift-click picks the run from the last day clicked. The caller acts on the
// selection (`selected` is a two-way model of yyyy-MM-dd keys).
export class MonthCalendarComponent {
  private readonly locale = inject(LOCALE_ID);

  public readonly year = input.required<number>();
  // 1-12.
  public readonly month = input.required<number>();
  public readonly days = input<MonthCalendarDay[]>([]);
  public readonly selectable = input(true);
  public readonly selected = model<string[]>([]);

  public readonly monthChange = output<{ year: number; month: number }>();

  private anchor: string | null = null;

  protected readonly title = computed(() =>
    formatDate(new Date(this.year(), this.month() - 1, 1), 'MMMM y', this.locale),
  );

  // Monday first: 2 November 2026 is a Monday.
  protected readonly weekdayNames = computed(() =>
    Array.from({ length: 7 }, (_, index) =>
      formatDate(addCalendarDays(new Date(2026, 10, 2), index), 'EEE', this.locale),
    ),
  );

  protected readonly weeks = computed<MonthCalendarCell[][]>(() => {
    const first = new Date(this.year(), this.month() - 1, 1);
    const last = new Date(this.year(), this.month(), 0);
    const byKey = new Map(this.days().map((day) => [day.date.slice(0, 10), day]));
    const today = toDayKey(new Date());

    const weeks: MonthCalendarCell[][] = [];
    const end = addCalendarDays(last, 6 - ((last.getDay() + 6) % 7));

    for (let date = mondayOfWeek(first); date <= end; date = addCalendarDays(date, 1)) {
      if (date.getDay() === 1) {
        weeks.push([]);
      }

      const key = toDayKey(date);
      weeks[weeks.length - 1].push({
        key,
        dayNumber: date.getDate(),
        inMonth: date.getMonth() === first.getMonth(),
        isToday: key === today,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        day: byKey.get(key),
      });
    }

    return weeks;
  });

  protected isSelected(cell: MonthCalendarCell): boolean {
    return this.selected().includes(cell.key);
  }

  protected toneClass(cell: MonthCalendarCell): string {
    return cell.day?.tone ? `plan-tone-${cell.day.tone}` : '';
  }

  protected fillPercent(cell: MonthCalendarCell): number {
    return Math.max(0, Math.min(1, cell.day?.fill ?? 0)) * 100;
  }

  protected shownChips(cell: MonthCalendarCell): string[] {
    return (cell.day?.chips ?? []).slice(0, 3);
  }

  protected moreChips(cell: MonthCalendarCell): number {
    return Math.max(0, (cell.day?.chips ?? []).length - 3);
  }

  protected step(months: number) {
    const date = new Date(this.year(), this.month() - 1 + months, 1);
    this.monthChange.emit({ year: date.getFullYear(), month: date.getMonth() + 1 });
  }

  protected thisMonth() {
    const now = new Date();
    this.monthChange.emit({ year: now.getFullYear(), month: now.getMonth() + 1 });
  }

  protected pick(event: MouseEvent, cell: MonthCalendarCell) {
    if (!this.selectable() || !cell.inMonth) {
      return;
    }

    const current = this.selected();

    if (event.shiftKey && this.anchor) {
      const from = fromDayKey(this.anchor);
      const to = fromDayKey(cell.key);
      const [start, end] = from <= to ? [from, to] : [to, from];
      const run: string[] = [];

      for (let date = start; date <= end; date = addCalendarDays(date, 1)) {
        run.push(toDayKey(date));
      }

      this.selected.set(run);
      return;
    }

    this.anchor = cell.key;

    if (event.ctrlKey || event.metaKey) {
      this.selected.set(
        current.includes(cell.key) ? current.filter((key) => key !== cell.key) : [...current, cell.key],
      );
      return;
    }

    this.selected.set(current.length === 1 && current[0] === cell.key ? [] : [cell.key]);
  }
}
